// ==============================|| REACT & THIRD-PARTY IMPORTS ||============================== //
import React from 'react';
import { Dialog, Box, Typography, IconButton, Button, Stack, useTheme, alpha } from '@mui/material';

// ==============================|| ANT DESIGN ICONS IMPORTS ||============================== //
import { CloseOutlined, DownloadOutlined, WarningOutlined } from '@ant-design/icons';

// ==============================|| TYPES & INTERFACES ||============================== //
export interface VideoPreviewProps {
    videoUrl: string;
    videoTitle: string;
    onClose: () => void;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({ videoUrl, videoTitle, onClose }) => {
    const theme = useTheme();
    const fileExt = videoTitle.split('.').pop()?.toLowerCase() || '';
    const isPlayable = ['mp4', 'mov', 'webm', 'ogg'].includes(fileExt);

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = videoUrl;
        link.download = videoTitle;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Dialog
            open={true}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    onClose();
                }
            }}
            maxWidth="md"
            fullWidth
            scroll="body"
            PaperProps={{
                sx: {
                    bgcolor: '#0a0a0a',
                    color: '#fff',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.5)',
                    border: '1px solid #2a2a2a'
                }
            }}
            sx={{
                zIndex: 10000,
                '& .MuiDialog-backdrop': {
                    backdropFilter: 'blur(8px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)'
                }
            }}
        >
            {/* Header */}
            <Box sx={{
                position: 'relative',
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: '#141414',
                borderBottom: '1px solid #222222'
            }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#fff', pr: 4 }}>
                    {videoTitle}
                </Typography>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#8c8c8c',
                        '&:hover': {
                            color: '#fff',
                            bgcolor: 'rgba(255,255,255,0.08)'
                        }
                    }}
                >
                    <CloseOutlined />
                </IconButton>
            </Box>

            {/* Content Body */}
            <Box sx={{
                width: '100%',
                minHeight: isPlayable ? 'auto' : '300px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: '#000',
                p: 3
            }}>
                {isPlayable ? (
                    <video
                        src={videoUrl}
                        controls
                        autoPlay
                        style={{
                            width: '100%',
                            maxHeight: '65vh',
                            objectFit: 'contain',
                            borderRadius: '8px'
                        }}
                    />
                ) : (
                    <Stack spacing={3} alignItems="center" justifyContent="center" sx={{ maxWidth: '450px', textAlign: 'center', py: 2 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            //bgcolor: alpha(theme.palette.warning[300], 0.15),
                            bgcolor: theme.palette.warning.lighter,
                            color: theme.palette.warning.main,
                            mb: 1
                        }}>
                            <WarningOutlined style={{ fontSize: '32px' }} />
                        </Box>

                        <Stack spacing={1}>
                            <Typography variant="h5" fontWeight={600} sx={{ color: '#fff' }}>
                                Video Format Not Supported
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.grey[400], lineHeight: 1.6 }}>
                                This <strong>.{fileExt}</strong> video format cannot be played directly in your browser.
                                Please download the file and open it with a media player such as VLC.
                            </Typography>
                        </Stack>

                        <Stack direction="row" spacing={2} sx={{ width: '100%', pt: 1 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="large"
                                onClick={handleDownload}
                                startIcon={<DownloadOutlined style={{ fontSize: '18px' }} />}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Download Video
                            </Button>
                            <Button
                                variant="outlined"
                                fullWidth
                                size="large"
                                onClick={onClose}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    borderColor: '#444',
                                    color: '#ccc',
                                    '&:hover': {
                                        borderColor: '#666',
                                        bgcolor: 'rgba(255,255,255,0.05)'
                                    }
                                }}
                            >
                                Close
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </Box>
        </Dialog>
    );
};

export default VideoPreview;
